import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../Layout';
import aoAPI from '../../api/aoAPI';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const EMPTY_FORM = { name: '', code: '', inCharge: '', fiscalYear: new Date().getFullYear() };

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h3 className="font-semibold text-gray-800">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>
);

const AOClientMaintenance = () => {
  const [clients, setClients]   = useState([]);
  const [staff, setStaff]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [filterIC, setFilterIC] = useState('');

  // Modal state
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [cRes, sRes] = await Promise.all([aoAPI.getClients(), aoAPI.getStaff()]);
      setClients(cRes.data || []);
      setStaff(sRes.data || []);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setModal(true);
  };

  const openEdit = (c) => {
    setEditId(c._id);
    setForm({
      name:       c.name,
      code:       c.code || '',
      inCharge:   c.inCharge?._id || '',
      fiscalYear: c.fiscalYear || new Date().getFullYear(),
    });
    setFormError('');
    setModal(true);
  };

  const closeModal = () => { setModal(false); setFormError(''); };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError('Client name is required'); return; }
    try {
      setSaving(true);
      setFormError('');
      const payload = {
        name:       form.name.trim(),
        code:       form.code.trim(),
        inCharge:   form.inCharge || null,
        fiscalYear: parseInt(form.fiscalYear, 10),
      };
      if (editId) {
        await aoAPI.updateClient(editId, payload);
      } else {
        await aoAPI.createClient(payload);
      }
      closeModal();
      await load();
    } catch (e) {
      setFormError(typeof e === 'string' ? e : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this client?')) return;
    try {
      await aoAPI.deleteClient(id);
      await load();
    } catch (e) {
      alert(typeof e === 'string' ? e : 'Failed to deactivate');
    }
  };

  const filtered = clients.filter(c => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.code || '').toLowerCase().includes(search.toLowerCase());
    const matchIC     = !filterIC || c.inCharge?._id === filterIC;
    return matchSearch && matchIC;
  });

  return (
    <Layout pageTitle="Client Maintenance">
      <div className="p-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Clients</h2>
            <p className="text-xs text-gray-500 mt-0.5">Manage clients and their in-charge assignments.</p>
          </div>
          <button
            onClick={openAdd}
            className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm px-4 py-2 rounded-lg transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add Client
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or code…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <select
            value={filterIC}
            onChange={e => setFilterIC(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">All In-Charge</option>
            {staff.map(s => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
        </div>

        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

        {/* Table */}
        {loading ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-12">No clients found.</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Client Name</th>
                  <th className="px-4 py-3 text-left">Code</th>
                  <th className="px-4 py-3 text-left">In-Charge</th>
                  <th className="px-4 py-3 text-center">Fiscal Year</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filtered.map(c => (
                  <tr key={c._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{c.name}</td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">{c.code || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{c.inCharge?.name || '—'}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{c.fiscalYear}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${c.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                        {c.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openEdit(c)} className="text-gray-400 hover:text-teal-600" title="Edit">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDeactivate(c._id)} className="text-gray-400 hover:text-red-500" title="Deactivate">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal */}
        {modal && (
          <Modal title={editId ? 'Edit Client' : 'New Client'} onClose={closeModal}>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Client Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g. ABC Corporation"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Code</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g. ABC-001"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">In-Charge</label>
                <select
                  value={form.inCharge}
                  onChange={e => setForm(f => ({ ...f, inCharge: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">— None —</option>
                  {staff.map(s => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fiscal Year</label>
                <input
                  type="number"
                  value={form.fiscalYear}
                  onChange={e => setForm(f => ({ ...f, fiscalYear: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  min={2000}
                  max={2100}
                />
              </div>

              {formError && <p className="text-xs text-red-500">{formError}</p>}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white text-sm py-2 rounded-lg disabled:opacity-60"
                >
                  {saving ? 'Saving…' : editId ? 'Update Client' : 'Create Client'}
                </button>
                <button
                  onClick={closeModal}
                  className="flex-1 border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm py-2 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </Layout>
  );
};

export default AOClientMaintenance;
